import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { Button } from '@/components/ui/Button';
import { ProgressIndicator } from '../CampaignCreation/ProgressIndicator';
import { AdGroupBasicsStep } from './steps/AdGroupBasicsStep';
import { AdGroupBiddingStep } from './steps/AdGroupBiddingStep';
import { AdCreationStep } from './steps/AdCreationStep';
import { DisplayAdCreationStep, type DisplayAdFormData } from './steps/DisplayAdCreationStep';
import { DemandGenAdCreationStep, type DemandGenAdFormData } from './steps/DemandGenAdCreationStep';
import { DEFAULT_DEMAND_GEN_AD_FORM_DATA } from './steps/demandGenAdFormDefaults';
import { ShoppingAdInfoStep } from './steps/ShoppingAdInfoStep';
import { AdGroupReviewStep } from './steps/AdGroupReviewStep';
import { campaignsApi } from '@/services/api/campaigns';
import { useMaterializePendingTargeted } from '@/hooks/useMaterializePendingTargeted';
import { batchDeleteImages } from '@/services/api/media';
import type { PendingTargeted } from '@/types/media';
import { debug } from '@/utils/debug';
import { isUrlGloballyAuthorized } from '@/utils/urlUtils';
import { useAuthStore } from '@/store/authStore';
import { formatBiddingStrategy } from '@/utils/formatEnum';
import type { Campaign } from '@/types/campaign';
import type { AppConfiguration } from '@/types/api';
import {
  dollarsToMicros,
  type AdGroupFormData,
  type BiddingStrategy,
  type CreateAdGroupRequest,
} from '@/types/adGroup';

interface CreateAdGroupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  campaignId: string;
  campaignName: string;
  campaignType?: string;
  campaignBiddingStrategy?: string;
}

interface FormErrors {
  name?: string;
  cpcBid?: string;
  targetCpa?: string;
  targetRoas?: string;
  // RSA errors
  headlines?: string;
  descriptions?: string;
  finalUrl?: string;
  // RDA errors
  landscapeImages?: string;
  squareImages?: string;
  shortHeadlines?: string;
  longHeadline?: string;
  businessName?: string;
  // Demand Gen errors
  logoImages?: string;
  videoIds?: string;
  carouselCards?: string;
  longHeadlines?: string;
}

// Helper to determine ad type from campaign type
function getAdTypeForCampaign(campaignType?: string): 'RSA' | 'RDA' | 'SHOPPING' | 'DEMAND_GEN' {
  if (!campaignType) return 'RSA';

  if (campaignType.includes('DISPLAY')) {
    return 'RDA';
  }
  if (campaignType.includes('SHOPPING')) {
    return 'SHOPPING';
  }
  if (campaignType.includes('DEMAND_GEN')) {
    return 'DEMAND_GEN';
  }
  // SEARCH, AI_MAX_SEARCH, and others default to RSA
  return 'RSA';
}

// Get step label for ad creation based on campaign type
function getAdStepLabel(adType: 'RSA' | 'RDA' | 'SHOPPING' | 'DEMAND_GEN'): string {
  switch (adType) {
    case 'RDA':
      return 'Display Ad';
    case 'SHOPPING':
      return 'Ad Info';
    case 'DEMAND_GEN':
      return 'Demand Gen Ad';
    default:
      return 'Create Ad';
  }
}

export const CreateAdGroupWizard: React.FC<CreateAdGroupWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaignId,
  campaignName,
  campaignType,
  campaignBiddingStrategy,
}) => {
  const queryClient = useQueryClient();
  const { memberUuid } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const materializePending = useMaterializePendingTargeted();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Determine ad type based on campaign type
  const adType = useMemo(() => getAdTypeForCampaign(campaignType), [campaignType]);

  // RSA form data (for Search campaigns)
  const [formData, setFormData] = useState<AdGroupFormData>({
    name: '',
    status: 'ENABLED',
    bidding_strategy: 'INHERIT',
    cpc_bid_dollars: '',
    target_cpa_dollars: '',
    target_roas: '',
    headlines: ['', '', ''],
    descriptions: ['', ''],
    finalUrl: '',
    path1: '',
    path2: '',
  });

  // RDA form data (for Display campaigns)
  const [displayFormData, setDisplayFormData] = useState<DisplayAdFormData>({
    landscapePending: [],
    squarePending: [],
    logoSquarePending: [],
    logoLandscapePending: [],
    shortHeadlines: [''],
    longHeadline: '',
    descriptions: [''],
    businessName: '',
    finalUrl: '',
  });

  // Demand Gen form data (for Demand Gen campaigns)
  const [demandGenFormData, setDemandGenFormData] = useState<DemandGenAdFormData>(DEFAULT_DEMAND_GEN_AD_FORM_DATA);

  const [errors, setErrors] = useState<FormErrors>({});

  // Dynamic wizard steps based on campaign type
  const wizardSteps = useMemo(() => [
    { label: 'Basics', description: '' },
    { label: 'Bidding', description: '' },
    { label: getAdStepLabel(adType), description: '' },
    { label: 'Review', description: '' },
  ], [adType]);

  // Reset form when wizard opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setErrors({});
      setSubmitError(null);
      setFormData({
        name: '',
        status: 'ENABLED',
        bidding_strategy: 'INHERIT',
        cpc_bid_dollars: '',
        target_cpa_dollars: '',
        target_roas: '',
        headlines: ['', '', ''],
        descriptions: ['', ''],
        finalUrl: '',
        path1: '',
        path2: '',
      });
      setDisplayFormData({
        landscapePending: [],
        squarePending: [],
        logoSquarePending: [],
        logoLandscapePending: [],
        shortHeadlines: [''],
        longHeadline: '',
        descriptions: [''],
        businessName: '',
        finalUrl: '',
      });
      setDemandGenFormData(DEFAULT_DEMAND_GEN_AD_FORM_DATA);
    }
  }, [isOpen]);

  // Validation function for each step
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    switch (step) {
      case 1: // Basics
        if (!formData.name.trim()) {
          newErrors.name = 'Ad group name is required';
        } else if (formData.name.length > 255) {
          newErrors.name = 'Ad group name must be 255 characters or less';
        }
        break;

      case 2: { // Bidding
        if (formData.bidding_strategy === 'MANUAL_CPC') {
          if (!formData.cpc_bid_dollars) {
            newErrors.cpcBid = 'CPC bid is required for Manual CPC bidding';
          } else {
            const cpcBid = parseFloat(formData.cpc_bid_dollars);
            if (isNaN(cpcBid)) {
              newErrors.cpcBid = 'CPC bid must be a valid number';
            } else if (cpcBid <= 0) {
              newErrors.cpcBid = 'CPC bid must be greater than $0';
            } else if (cpcBid > 1000) {
              newErrors.cpcBid = 'Maximum CPC bid is $1,000';
            }
          }
        } else if (formData.bidding_strategy === 'TARGET_CPA') {
          if (!formData.target_cpa_dollars) {
            newErrors.targetCpa = 'Target CPA is required for Target CPA bidding';
          } else {
            const targetCpa = parseFloat(formData.target_cpa_dollars);
            if (isNaN(targetCpa)) {
              newErrors.targetCpa = 'Target CPA must be a valid number';
            } else if (targetCpa <= 0) {
              newErrors.targetCpa = 'Target CPA must be greater than $0';
            } else if (targetCpa > 1000000) {
              newErrors.targetCpa = 'Maximum target CPA is $1,000,000';
            }
          }
        } else if (formData.bidding_strategy === 'TARGET_ROAS') {
          if (!formData.target_roas) {
            newErrors.targetRoas = 'Target ROAS is required for Target ROAS bidding';
          } else {
            const targetRoas = parseFloat(formData.target_roas);
            if (isNaN(targetRoas)) {
              newErrors.targetRoas = 'Target ROAS must be a valid number';
            } else if (targetRoas <= 0) {
              newErrors.targetRoas = 'Target ROAS must be greater than 0';
            } else if (targetRoas > 1000) {
              newErrors.targetRoas = 'Maximum target ROAS is 1000x';
            }
          }
        }
        break;
      }

      case 3: { // Ad Creation - varies by campaign type
        if (adType === 'RSA') {
          // Validate RSA
          if (!formData.finalUrl.trim()) {
            newErrors.finalUrl = 'Final URL is required';
          } else if (!formData.finalUrl.startsWith('http://') && !formData.finalUrl.startsWith('https://')) {
            newErrors.finalUrl = 'Final URL must start with http:// or https://';
          }

          const nonEmptyHeadlines = formData.headlines.filter((h) => h.trim().length > 0);
          if (nonEmptyHeadlines.length < 3) {
            newErrors.headlines = 'At least 3 headlines are required';
          } else if (formData.headlines.some((h) => h.trim().length > 0 && h.trim().length > 30)) {
            newErrors.headlines = 'Headlines must be 30 characters or less';
          }

          const nonEmptyDescriptions = formData.descriptions.filter((d) => d.trim().length > 0);
          if (nonEmptyDescriptions.length < 2) {
            newErrors.descriptions = 'At least 2 descriptions are required';
          } else if (formData.descriptions.some((d) => d.trim().length > 0 && d.trim().length > 90)) {
            newErrors.descriptions = 'Descriptions must be 90 characters or less';
          }
        } else if (adType === 'RDA') {
          // Validate RDA
          if (displayFormData.landscapePending.length === 0) {
            newErrors.landscapeImages = 'At least 1 landscape image is required';
          }
          if (displayFormData.squarePending.length === 0) {
            newErrors.squareImages = 'At least 1 square image is required';
          }

          const nonEmptyShortHeadlines = displayFormData.shortHeadlines.filter((h) => h.trim().length > 0);
          if (nonEmptyShortHeadlines.length < 1) {
            newErrors.shortHeadlines = 'At least 1 short headline is required';
          } else if (displayFormData.shortHeadlines.some((h) => h.trim().length > 0 && h.trim().length > 30)) {
            newErrors.shortHeadlines = 'Short headlines must be 30 characters or less';
          }

          if (!displayFormData.longHeadline.trim()) {
            newErrors.longHeadline = 'Long headline is required';
          } else if (displayFormData.longHeadline.length > 90) {
            newErrors.longHeadline = 'Long headline must be 90 characters or less';
          }

          const nonEmptyDescriptionsRDA = displayFormData.descriptions.filter((d) => d.trim().length > 0);
          if (nonEmptyDescriptionsRDA.length < 1) {
            newErrors.descriptions = 'At least 1 description is required';
          } else if (displayFormData.descriptions.some((d) => d.trim().length > 0 && d.trim().length > 90)) {
            newErrors.descriptions = 'Descriptions must be 90 characters or less';
          }

          if (!displayFormData.businessName.trim()) {
            newErrors.businessName = 'Business name is required';
          } else if (displayFormData.businessName.length > 25) {
            newErrors.businessName = 'Business name must be 25 characters or less';
          }

          if (!displayFormData.finalUrl.trim()) {
            newErrors.finalUrl = 'Final URL is required';
          } else if (!displayFormData.finalUrl.startsWith('http://') && !displayFormData.finalUrl.startsWith('https://')) {
            newErrors.finalUrl = 'Final URL must start with http:// or https://';
          }
        } else if (adType === 'DEMAND_GEN') {
          // Validate Demand Gen ads based on ad type
          // Common validation - business name required for all types
          if (!demandGenFormData.businessName.trim()) {
            newErrors.businessName = 'Business name is required';
          } else if (demandGenFormData.businessName.length > 25) {
            newErrors.businessName = 'Business name must be 25 characters or less';
          }

          if (demandGenFormData.adType === 'MULTI_ASSET') {
            // Multi-Asset: images, headlines, descriptions, final URL
            if (demandGenFormData.landscapePending.length === 0) {
              newErrors.landscapeImages = 'At least 1 landscape image is required';
            }
            if (demandGenFormData.squarePending.length === 0) {
              newErrors.squareImages = 'At least 1 square image is required';
            }
            if (demandGenFormData.logoPending.length === 0) {
              newErrors.logoImages = 'At least 1 logo image is required';
            }
            const nonEmptyHeadlinesDG = demandGenFormData.headlines.filter((h) => h.trim().length > 0);
            if (nonEmptyHeadlinesDG.length < 1) {
              newErrors.headlines = 'At least 1 headline is required';
            } else if (demandGenFormData.headlines.some((h) => h.trim().length > 0 && h.trim().length > 40)) {
              newErrors.headlines = 'Headlines must be 40 characters or less';
            }
            const nonEmptyDescriptionsDG = demandGenFormData.descriptions.filter((d) => d.trim().length > 0);
            if (nonEmptyDescriptionsDG.length < 1) {
              newErrors.descriptions = 'At least 1 description is required';
            } else if (demandGenFormData.descriptions.some((d) => d.trim().length > 0 && d.trim().length > 90)) {
              newErrors.descriptions = 'Descriptions must be 90 characters or less';
            }
            if (!demandGenFormData.finalUrl.trim()) {
              newErrors.finalUrl = 'Final URL is required';
            } else if (!demandGenFormData.finalUrl.startsWith('http://') && !demandGenFormData.finalUrl.startsWith('https://')) {
              newErrors.finalUrl = 'Final URL must start with http:// or https://';
            }
          } else if (demandGenFormData.adType === 'VIDEO') {
            // Video: video IDs, logos, headlines, long headlines, descriptions, final URL
            const nonEmptyVideos = demandGenFormData.videoIds.filter((v) => v.trim().length > 0);
            if (nonEmptyVideos.length < 1) {
              newErrors.videoIds = 'At least 1 YouTube video is required';
            }
            if (demandGenFormData.logoPending.length === 0) {
              newErrors.logoImages = 'At least 1 logo image is required';
            }
            const nonEmptyHeadlinesVideo = demandGenFormData.headlines.filter((h) => h.trim().length > 0);
            if (nonEmptyHeadlinesVideo.length < 1) {
              newErrors.headlines = 'At least 1 headline is required';
            }
            const nonEmptyLongHeadlines = demandGenFormData.longHeadlines.filter((h) => h.trim().length > 0);
            if (nonEmptyLongHeadlines.length < 1) {
              newErrors.longHeadlines = 'At least 1 long headline is required';
            }
            const nonEmptyDescriptionsVideo = demandGenFormData.descriptions.filter((d) => d.trim().length > 0);
            if (nonEmptyDescriptionsVideo.length < 1) {
              newErrors.descriptions = 'At least 1 description is required';
            }
            if (!demandGenFormData.finalUrl.trim()) {
              newErrors.finalUrl = 'Final URL is required';
            }
          } else if (demandGenFormData.adType === 'CAROUSEL') {
            // Carousel: 2-10 cards, each with image, headline, URL
            if (demandGenFormData.carouselCards.length < 2) {
              newErrors.carouselCards = 'At least 2 carousel cards are required';
            } else {
              const invalidCards = demandGenFormData.carouselCards.filter(
                (card) => !card.pending || !card.headline.trim() || !card.finalUrl.trim()
              );
              if (invalidCards.length > 0) {
                newErrors.carouselCards = 'Each card must have an image, headline, and URL';
              }
            }
          }
        }
        // SHOPPING - no validation needed, ads come from feed
        break;
      }

      case 4: // Review - no validation needed
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, wizardSteps.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Materialize all pending crops just before submit. This is the
      // ONLY place GCS Targeted rows get created in the creation flow —
      // abandoned wizard runs leave zero orphans. `materializePending`
      // handles partial-failure cleanup within a single bucket; this
      // function additionally cleans up across all 9 buckets if the
      // subsequent `createAdGroup` call fails (see catch block below).
      const validCarousel = demandGenFormData.carouselCards.filter(
        (c) => c.pending && c.headline.trim() && c.finalUrl.trim(),
      );
      const [
        rdaLandscapeM, rdaSquareM, rdaLogoSqM, rdaLogoLandM,
        dgLandscapeM, dgSquareM, dgPortraitM, dgLogoM,
        carouselM,
      ] = await Promise.all([
        // RDA (Display) buckets
        materializePending(adType === 'RDA' ? displayFormData.landscapePending : []),
        materializePending(adType === 'RDA' ? displayFormData.squarePending : []),
        materializePending(adType === 'RDA' ? displayFormData.logoSquarePending : []),
        materializePending(adType === 'RDA' ? displayFormData.logoLandscapePending : []),
        // Demand Gen buckets
        materializePending(adType === 'DEMAND_GEN' ? demandGenFormData.landscapePending : []),
        materializePending(adType === 'DEMAND_GEN' ? demandGenFormData.squarePending : []),
        materializePending(adType === 'DEMAND_GEN' ? demandGenFormData.portraitPending : []),
        materializePending(adType === 'DEMAND_GEN' ? demandGenFormData.logoPending : []),
        materializePending(
          adType === 'DEMAND_GEN' && demandGenFormData.adType === 'CAROUSEL'
            ? validCarousel.map((c) => c.pending).filter((p): p is PendingTargeted => p !== null)
            : [],
        ),
      ]);
      // Union of every materialized id, for cleanup if the submit fails.
      const allMaterializedIds = [
        ...rdaLandscapeM, ...rdaSquareM, ...rdaLogoSqM, ...rdaLogoLandM,
        ...dgLandscapeM, ...dgSquareM, ...dgPortraitM, ...dgLogoM,
        ...carouselM,
      ].map((t) => t.id);
      const cleanupMaterialized = async () => {
        if (allMaterializedIds.length === 0 || !memberUuid) return;
        try {
          await batchDeleteImages({
            member_uuid: memberUuid,
            image_ids: allMaterializedIds,
          });
        } catch (cleanupErr) {
          // Swallow — we want the original submit error to surface, not
          // a cleanup failure. The orphaned rows can be GC'd later.
          debug.error('CreateAdGroupWizard: cleanup of materialized Targeted ids failed', cleanupErr);
        }
      };

      // From here on, any failure means we created Targeted rows that
      // never got linked to an ad group — best-effort delete them before
      // rethrowing so the orphan-free invariant holds.
      try {
      // Build API request based on ad type
      const request: CreateAdGroupRequest = {
        name: formData.name.trim(),
        status: formData.status,
        bidding_strategy: formData.bidding_strategy,
        ad: adType === 'SHOPPING' ? {
          // Shopping campaigns don't need ad data
          headlines: [],
          descriptions: [],
          final_url: '',
        } : adType === 'RDA' ? {
          // Display campaign - RDA data
          headlines: displayFormData.shortHeadlines.filter((h) => h.trim().length > 0),
          descriptions: displayFormData.descriptions.filter((d) => d.trim().length > 0),
          final_url: displayFormData.finalUrl.trim(),
          // RDA-specific fields (will be added to request type)
          long_headline: displayFormData.longHeadline.trim(),
          business_name: displayFormData.businessName.trim(),
          landscape_image_ids: rdaLandscapeM.map((t) => t.id),
          square_image_ids: rdaSquareM.map((t) => t.id),
          logo_square_ids: rdaLogoSqM.map((t) => t.id),
          logo_landscape_ids: rdaLogoLandM.map((t) => t.id),
        } : adType === 'DEMAND_GEN' ? {
          // Demand Gen campaign - varies by ad type
          ad_name: demandGenFormData.adName?.trim() || undefined,
          headlines: demandGenFormData.headlines.filter((h) => h.trim().length > 0),
          descriptions: demandGenFormData.descriptions.filter((d) => d.trim().length > 0),
          final_url: demandGenFormData.finalUrl.trim(),
          business_name: demandGenFormData.businessName.trim(),
          call_to_action: demandGenFormData.callToAction,
          // Demand Gen specific fields
          demand_gen_ad_type: demandGenFormData.adType,
          // Multi-Asset fields
          landscape_image_ids: dgLandscapeM.map((t) => t.id),
          square_image_ids: dgSquareM.map((t) => t.id),
          portrait_image_ids: dgPortraitM.map((t) => t.id),
          logo_image_ids: dgLogoM.map((t) => t.id),
          // Video fields
          video_ids: demandGenFormData.videoIds.filter((v) => v.trim().length > 0),
          long_headlines: demandGenFormData.longHeadlines.filter((h) => h.trim().length > 0),
          // Carousel fields — re-zip the materialized Targeted ids back to
          // the cards they came from, in order.
          carousel_cards: validCarousel.map((card, i) => ({
            headline: card.headline.trim(),
            image_id: carouselM[i].id,
            final_url: card.finalUrl.trim(),
          })),
        } : {
          // Search campaign - RSA data
          headlines: formData.headlines.filter((h) => h.trim().length > 0),
          descriptions: formData.descriptions.filter((d) => d.trim().length > 0),
          final_url: formData.finalUrl.trim(),
          path1: formData.path1?.trim() || undefined,
          path2: formData.path2?.trim() || undefined,
        },
      };

      // Add campaign type to help backend determine which ad to create
      (request as CreateAdGroupRequest & { campaign_type?: string }).campaign_type = campaignType;

      // Add bidding-specific parameters
      if (formData.bidding_strategy === 'MANUAL_CPC' && formData.cpc_bid_dollars) {
        request.cpc_bid_micros = dollarsToMicros(formData.cpc_bid_dollars);
      } else if (formData.bidding_strategy === 'TARGET_CPA' && formData.target_cpa_dollars) {
        request.target_cpa_micros = dollarsToMicros(formData.target_cpa_dollars);
      } else if (formData.bidding_strategy === 'TARGET_ROAS' && formData.target_roas) {
        request.target_roas = parseFloat(formData.target_roas);
      }

      debug.log('CreateAdGroupWizard: Submitting ad group creation request', { request, adType, campaignType });

      const response = await campaignsApi.createAdGroup(campaignId, request);

      debug.log('CreateAdGroupWizard: Ad group created successfully', response);

      // Check if URL is globally authorized in existing config
      const existingConfig = memberUuid ? queryClient.getQueryData<AppConfiguration>(['app-config', memberUuid]) : null;
      const finalUrl = adType === 'RDA' ? displayFormData.finalUrl
        : adType === 'DEMAND_GEN' ? demandGenFormData.finalUrl
        : formData.finalUrl;
      const urlIsAuthorized = isUrlGloballyAuthorized(finalUrl, existingConfig);
      debug.log('CreateAdGroupWizard: URL authorization check', { url: finalUrl, urlIsAuthorized });

      // Build optimistic ad data based on ad type
      let optimisticAd: {
        ad_id: string;
        ad_type: string;
        ad_name?: string;
        headlines?: string[];
        descriptions?: string[];
        final_urls?: Array<{ url: string; flouri_scraping_enabled: boolean }>;
        rda_headlines?: string[];
        rda_descriptions?: string[];
        rda_long_headline?: string;
        rda_business_name?: string;
        rda_marketing_images?: string[];
        rda_square_marketing_images?: string[];
        rda_logo_images?: string[];
      } | null = null;

      if (adType === 'RDA' && displayFormData) {
        // Display campaign - create optimistic RDA
        optimisticAd = {
          ad_id: `optimistic_${Date.now()}`, // Temporary ID until data sync
          ad_type: 'RESPONSIVE_DISPLAY_AD',
          ad_name: displayFormData.longHeadline || displayFormData.shortHeadlines[0] || 'New Display Ad',
          headlines: displayFormData.shortHeadlines.filter((h) => h.trim()),
          descriptions: displayFormData.descriptions.filter((d) => d.trim()),
          final_urls: [{ url: displayFormData.finalUrl, flouri_scraping_enabled: urlIsAuthorized }],
          rda_headlines: displayFormData.shortHeadlines.filter((h) => h.trim()),
          rda_descriptions: displayFormData.descriptions.filter((d) => d.trim()),
          rda_long_headline: displayFormData.longHeadline,
          rda_business_name: displayFormData.businessName,
          // Note: Image IDs are Media Library IDs, not Google Ads asset IDs
          // These will be replaced with actual asset IDs after data sync
          rda_marketing_images: rdaLandscapeM.map((t) => t.id),
          rda_square_marketing_images: rdaSquareM.map((t) => t.id),
          rda_logo_images: rdaLogoSqM.map((t) => t.id),
        };
      } else if (adType === 'DEMAND_GEN' && demandGenFormData) {
        // Demand Gen campaign - create optimistic Demand Gen ad
        const dgAdTypeMap = {
          'MULTI_ASSET': 'DEMAND_GEN_MULTI_ASSET_AD',
          'VIDEO': 'DEMAND_GEN_VIDEO_RESPONSIVE_AD',
          'CAROUSEL': 'DEMAND_GEN_CAROUSEL_AD',
        };
        optimisticAd = {
          ad_id: `optimistic_${Date.now()}`,
          ad_type: dgAdTypeMap[demandGenFormData.adType] || 'DEMAND_GEN_MULTI_ASSET_AD',
          ad_name: demandGenFormData.adName || demandGenFormData.headlines[0] || demandGenFormData.businessName || 'New Demand Gen Ad',
          headlines: demandGenFormData.headlines.filter((h) => h.trim()),
          descriptions: demandGenFormData.descriptions.filter((d) => d.trim()),
          final_urls: demandGenFormData.adType === 'CAROUSEL'
            ? demandGenFormData.carouselCards.map((card) => ({
                url: card.finalUrl,
                flouri_scraping_enabled: isUrlGloballyAuthorized(card.finalUrl, existingConfig),
              }))
            : [{ url: demandGenFormData.finalUrl, flouri_scraping_enabled: urlIsAuthorized }],
        };
      } else if (adType === 'RSA') {
        // Search campaign - create optimistic RSA
        optimisticAd = {
          ad_id: `optimistic_${Date.now()}`,
          ad_type: 'RESPONSIVE_SEARCH_AD',
          ad_name: formData.headlines[0] || 'New Search Ad',
          headlines: formData.headlines.filter((h) => h.trim()),
          descriptions: formData.descriptions.filter((d) => d.trim()),
          final_urls: [{ url: formData.finalUrl, flouri_scraping_enabled: urlIsAuthorized }],
        };
      }
      // SHOPPING campaigns don't have optimistic ads - they come from feed

      const newAdGroup = {
        ad_group_id: response.ad_group_id,
        ad_group_name: response.ad_group_name,
        ads_group_id: response.ad_group_id,
        ads_group_name: response.ad_group_name,
        status: formData.status,
        // Format bidding strategy to match backend format (e.g., "MANUAL_CPC" -> "Manual CPC")
        // If INHERIT, use the campaign's bidding strategy since that's what Google Ads will use
        bidding_strategy_type: formData.bidding_strategy === 'INHERIT'
          ? formatBiddingStrategy(campaignBiddingStrategy)
          : formatBiddingStrategy(formData.bidding_strategy),
        max_cpc_bid: formData.cpc_bid_dollars ? parseFloat(formData.cpc_bid_dollars) : 0,
        flouri_optimize_bids: false,
        ads: optimisticAd ? [optimisticAd] : [],
        ads_list: optimisticAd ? [optimisticAd] : [],
        keywords_list: [],
      };

      // Optimistically update the campaign-from-cache query
      queryClient.setQueryData<Campaign>(['campaign-from-cache', campaignId], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          ads_group_list: [...(oldData.ads_group_list || []), newAdGroup],
        } as Campaign;
      });

      // Also update app-config cache since useCampaignFromCache derives from it
      if (memberUuid) {
        queryClient.setQueryData<AppConfiguration>(['app-config', memberUuid], (oldConfig) => {
          if (!oldConfig?.configuration?.campaign_list) return oldConfig;
          return {
            ...oldConfig,
            configuration: {
              ...oldConfig.configuration,
              campaign_list: oldConfig.configuration.campaign_list.map((camp) => {
                if (String(camp.campaign_id) === String(campaignId)) {
                  return {
                    ...camp,
                    ads_group_list: [...(camp.ads_group_list || []), newAdGroup],
                  };
                }
                return camp;
              }),
            },
          };
        });
      }

      debug.log('CreateAdGroupWizard: Optimistically updated campaign and app-config caches');

      // DON'T invalidate app-config - that would trigger a refetch from Google Ads which
      // may not have propagated the new ad group yet, overwriting our optimistic update.
      // Instead, just invalidate campaign-from-cache to trigger a re-derive from the
      // already-updated app-config cache.
      queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });

      await onSuccess();
      onClose();
      } catch (submitErr) {
        await cleanupMaterialized();
        throw submitErr;
      }
    } catch (error: unknown) {
      debug.error('CreateAdGroupWizard: Failed to create ad group', error);

      let errorMessage = 'Failed to create ad group. Please try again.';

      const apiError = error as {
        response?: { data?: { detail?: string; message?: string; error?: string } };
        message?: string;
      };
      if (apiError.response?.data) {
        const data = apiError.response.data;
        errorMessage = data.detail || data.message || data.error || errorMessage;

        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Create New Ad Group</h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a new ad group to <span className="font-medium">{campaignName}</span>
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={wizardSteps.length}
            steps={wizardSteps}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Step Content */}
        <div
          ref={scrollBodyRef}
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {currentStep === 1 && (
            <AdGroupBasicsStep
              name={formData.name}
              status={formData.status}
              onChangeName={(name) => setFormData((prev) => ({ ...prev, name }))}
              onChangeStatus={(status) => setFormData((prev) => ({ ...prev, status }))}
              errors={{ name: errors.name }}
            />
          )}

          {currentStep === 2 && (
            <AdGroupBiddingStep
              biddingStrategy={formData.bidding_strategy}
              campaignBiddingStrategy={campaignBiddingStrategy}
              cpcBidDollars={formData.cpc_bid_dollars}
              targetCpaDollars={formData.target_cpa_dollars}
              targetRoas={formData.target_roas}
              onChangeBiddingStrategy={(strategy: BiddingStrategy) =>
                setFormData((prev) => ({ ...prev, bidding_strategy: strategy }))
              }
              onChangeCpcBid={(value) => setFormData((prev) => ({ ...prev, cpc_bid_dollars: value }))}
              onChangeTargetCpa={(value) =>
                setFormData((prev) => ({ ...prev, target_cpa_dollars: value }))
              }
              onChangeTargetRoas={(value) => setFormData((prev) => ({ ...prev, target_roas: value }))}
              errors={{
                cpcBid: errors.cpcBid,
                targetCpa: errors.targetCpa,
                targetRoas: errors.targetRoas,
              }}
            />
          )}

          {currentStep === 3 && adType === 'RSA' && (
            <AdCreationStep
              headlines={formData.headlines}
              descriptions={formData.descriptions}
              finalUrl={formData.finalUrl}
              path1={formData.path1 || ''}
              path2={formData.path2 || ''}
              onChangeHeadlines={(headlines) => setFormData((prev) => ({ ...prev, headlines }))}
              onChangeDescriptions={(descriptions) =>
                setFormData((prev) => ({ ...prev, descriptions }))
              }
              onChangeFinalUrl={(finalUrl) => setFormData((prev) => ({ ...prev, finalUrl }))}
              onChangePath1={(path1) => setFormData((prev) => ({ ...prev, path1 }))}
              onChangePath2={(path2) => setFormData((prev) => ({ ...prev, path2 }))}
              errors={{
                headlines: errors.headlines,
                descriptions: errors.descriptions,
                finalUrl: errors.finalUrl,
              }}
            />
          )}

          {currentStep === 3 && adType === 'RDA' && (
            <DisplayAdCreationStep
              formData={displayFormData}
              onChangeFormData={(updates) => setDisplayFormData((prev) => ({ ...prev, ...updates }))}
              errors={{
                landscapeImages: errors.landscapeImages,
                squareImages: errors.squareImages,
                shortHeadlines: errors.shortHeadlines,
                longHeadline: errors.longHeadline,
                descriptions: errors.descriptions,
                businessName: errors.businessName,
                finalUrl: errors.finalUrl,
              }}
            />
          )}

          {currentStep === 3 && adType === 'SHOPPING' && (
            <ShoppingAdInfoStep />
          )}

          {currentStep === 3 && adType === 'DEMAND_GEN' && (
            <DemandGenAdCreationStep
              formData={demandGenFormData}
              onChangeFormData={(updates) => setDemandGenFormData((prev) => ({ ...prev, ...updates }))}
              errors={{
                headlines: errors.headlines,
                descriptions: errors.descriptions,
                longHeadlines: errors.longHeadline,
                landscapeImages: errors.landscapeImages,
                squareImages: errors.squareImages,
                logoImages: errors.logoImages,
                businessName: errors.businessName,
                finalUrl: errors.finalUrl,
                videoIds: errors.videoIds,
                carouselCards: errors.carouselCards,
              }}
            />
          )}

          {currentStep === 4 && (
            <AdGroupReviewStep
              formData={formData}
              campaignName={campaignName}
              campaignType={campaignType}
              adType={adType}
              displayFormData={adType === 'RDA' ? displayFormData : undefined}
              demandGenFormData={adType === 'DEMAND_GEN' ? demandGenFormData : undefined}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Step {currentStep} of {wizardSteps.length}
            </div>

            <div className="flex space-x-3">
              {currentStep > 1 && (
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}

              {currentStep < wizardSteps.length ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Ad Group'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
